import { cloneElement, isValidElement, type ReactNode, type Ref } from 'react';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { type IconProps } from '~/ui/icons/icon';
import { ChevronDownIcon } from '~/ui/icons/chevron-down';

type FilterTriggerProps = {
  ref?: Ref<HTMLButtonElement>;
  /** Optional leading icon (e.g. calendar on the date filter). */
  icon?: ReactNode;
  /** Label on the left (e.g. "Date", "Location", "Category"). */
  label: ReactNode;
  /** Value on the right (e.g. "All", "3 May 2026", "2 locations"). */
  value: ReactNode;
  /** Popover open state — flips `aria-expanded`. */
  open: boolean;
  /** What the popover anchored to this trigger holds — drives ARIA semantics. */
  popupKind: 'dialog' | 'listbox' | 'menu';
  onClick: () => void;
};

/**
 * Shared trigger button for the three home-page search-bar filters
 * (DateFilter, LocationFilter, CategoryFilter). The filters differ only in
 * their popover content; the chrome — button shape, label/value layout,
 * leading icon slot, trailing chevron, hover/focus styles — is identical
 * and lives here.
 *
 * The trigger is "naked" by design: no background, no border. The parent
 * `<SearchBar>` owns the surface (`surface.raised`) and the vertical
 * dividers between siblings; this trigger only contributes its own
 * spacing, focus ring, and hover affordance.
 */
export function FilterTrigger({
  ref,
  icon,
  label,
  value,
  open,
  popupKind,
  onClick,
}: FilterTriggerProps) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-haspopup={popupKind}
      className={triggerStyles}
    >
      {isValidElement<IconProps>(icon) && cloneElement(icon, { size: 'small' })}
      <Text style="body-large-regular" color="secondary" className={labelStyles}>
        {label}
      </Text>
      <Stack
        direction="row"
        align="center"
        gap="xsmall"
        className={valueStyles}
      >
        <Text
          style="body-large-regular"
          color="tertiary"
          className={valueTextStyles}
        >
          {value}
        </Text>
        <ChevronDownIcon size="small" />
      </Stack>
    </button>
  );
}

const triggerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'small',
  paddingX: 'medium',
  paddingY: '[12px]',
  width: 'full',
  cursor: 'interactive',
  textAlign: 'left',
  // No border-radius on the resting trigger — the SearchBar owns the outer
  // rounded chrome; a radius here would round the corners of the vertical
  // dividers between adjacent filters. The focus state gets a small radius
  // (outlines follow border-radius) so the focus ring isn't a hard
  // rectangle.
  _focusVisible: {
    borderRadius: 'small',
    outline: 'medium',
    outlineColor: 'border.focus',
    outlineOffset: 'tiny',
  },
});

const labelStyles = css({
  // Label keeps its natural width — "Date" / "Location" / "Category" are
  // short and stable. The value side absorbs all remaining space and
  // truncates when needed.
  flexShrink: '[0]',
  whiteSpace: 'nowrap',
});

const valueStyles = css({
  flex: '1',
  minWidth: '[0]',
  // Right-align so short values ("All") sit next to the chevron and long
  // values ellipsize against the chevron from the left.
  justifyContent: 'flex-end',
  overflow: 'hidden',
});

const valueTextStyles = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: '[0]',
});
